import {
    ElementRef,
    Input,
    Output,
    EventEmitter,
    HostBinding,
    OnChanges,
    SimpleChanges,
    ChangeDetectorRef,
    Renderer2,
    Directive,
    inject
} from "@angular/core";

import { ComponentPortal } from "@angular/cdk/portal";

import { LgConfirmationDialog } from "@logex/framework/ui-core";
import { LgTranslateService } from "@logex/framework/lg-localization";

import { IFlagsDefinition } from "./types";

import { IFlagged, IFlaggedWithDetails } from "@shared/types/types";
import { ControlValueAccessor } from "@angular/forms";

@Directive()
export abstract class FlagsMenuComponentBase<T> implements ControlValueAccessor, OnChanges {
    protected _changesDetector = inject(ChangeDetectorRef);
    protected _confirm = inject(LgConfirmationDialog);
    protected _elementRef = inject(ElementRef);
    protected _lgTranslate = inject(LgTranslateService);
    protected _renderer = inject(Renderer2);
    abstract current: IFlagged | IFlaggedWithDetails | null;

    @Input() definition: IFlagsDefinition;
    @Input() hideMixed = false;
    @Input() isDisabled = false;
    @Input() bulkUpdate = false;
    @Input() showWarningIfOverride = true;
    @Output() readonly currentFlagChange = new EventEmitter<T>();

    @HostBinding("class.active") _popupActive = false;

    protected _changed: (value: T) => void;
    protected _touched: () => void;
    private _visible = false;
    protected _value: T | undefined;
    protected _disabled = false;

    get visible(): boolean {
        return this._visible;
    }

    get value(): T | undefined {
        return this._value;
    }

    set value(value: T | undefined) {
        this._value = value;
    }

    get disabled(): boolean {
        return this._disabled;
    }

    writeValue(value: T): void {
        this._value = value;
        this._changesDetector.markForCheck();
    }

    registerOnChange(fn: any): void {
        this._changed = fn;
    }

    registerOnTouched(fn: any): void {
        this._touched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this._disabled = isDisabled;
        this.setDisabledStateInternal(this._disabled);
    }

    private setDisabledStateInternal(isDisabled: boolean) {
        if (isDisabled) {
            this._renderer.addClass(this._elementRef.nativeElement, "disabled");
        } else {
            this._renderer.removeClass(this._elementRef.nativeElement, "disabled");
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isDisabled) {
            this._disabled = this.isDisabled;
            this.setDisabledStateInternal(this._disabled);
        }
        if (changes.definition) {
            this._visible = this.definition && this.definition.length > 0;
        }
    }

    protected raiseFlagSaving(newValue: T): void {
        this.value = newValue;
        if (this._changed) {
            this._changed(newValue);
        }
        this.currentFlagChange.emit(newValue);
        this._changesDetector.markForCheck();
    }

    abstract _showFlagsOptionsPopup(event: Event): void;

    abstract _currentText(): string | ComponentPortal<any>;

    abstract _getColor(): number;

    abstract _hasColor: boolean;

    abstract _isMixedState: boolean;

    protected async flagChanged(newValue: T): Promise<boolean> {
        if (newValue !== this._value) {
            if (this._isMixedState && this.showWarningIfOverride) {
                const result = await this._confirm.confirm(
                    "",
                    this._lgTranslate.translate(".ComfirmDiscardChanges"),
                    {
                        button1: this._lgTranslate.translate(".Overwrite"),
                        button2: this._lgTranslate.translate(".Discard")
                    }
                );
                if (result === 1) {
                    this.raiseFlagSaving(newValue);
                    return true;
                }
            } else {
                this.raiseFlagSaving(newValue);
                return true;
            }
        }
        return false;
    }

    protected async bulkUpdateFlags(newValue: T): Promise<void> {
        if (newValue !== null) {
            const result = await this._confirm.confirm(
                "",
                this._lgTranslate.translate(".UpdateAllVisible"),
                {
                    button1: this._lgTranslate.translate(".Confirm"),
                    button2: this._lgTranslate.translate(".Discard"),
                    allowClose: true
                }
            );
            if (result === 1) this.raiseFlagSaving(newValue);
        }
    }
}
